import styled from "@emotion/styled";
import React, { ReactNode } from "react";
import { FullWidth } from "../core/BodyLayout";
import { breakpointNavBarFull } from "../nav-bar/Hamburger";
import {
  fontLargeHeadline,
  fontP3Regular,
} from "src/design-system/styles/fonts";
import Button from "src/design-system/components/Button/Button";
import ClinicalOralHeroImageDesktop from "./images/ClinicalOralHeroImageDesktop.webp";
import ClinicalOralHeroImageMobile from "./images/ClinicalOralHeroImageMobile.webp";
import GreenCheckmark from "./images/greenCheckmark.svg";
import {
  breakpointDesktopOnly,
  breakpointMobileExtended,
  breakpointWideDesktopOnly,
} from "src/design-system/styles/breakpoints";
import { trackBuyflowEvent } from "src/utils/api/tracker";
import { compassColors } from "src/utils/styling";
import i18n from "@i18n";
import { Text } from "src/components/core/Text";

import { ClinicalIntroContentProps } from "./ClinicalIntroContent/commonStyles";
import FsaHsaEligibility from "./FsaHsaEligibility";
import { isMetaTraffic } from "@utils/userSegment/features";

const Container = styled(FullWidth)`
  display: flex;
  flex-direction: column-reverse;
  background-color: ${compassColors.skyLite};
  @media ${breakpointDesktopOnly} {
    flex-direction: row;
    padding-bottom: 50px;
  }
`;

const IntroSection = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding-left: 32px;
  padding-right: 32px;
  width: 100%;
  @media ${breakpointNavBarFull} {
    padding-left: 80px;
    padding-right: 80px;
  }
  @media ${breakpointDesktopOnly} {
    min-width: 50vw;
  }
  @media ${breakpointWideDesktopOnly} {
    padding-left: 120px;
  }
`;

const Title = styled.p`
  ${fontLargeHeadline}
  font-weight: 400;
  margin-top: 55px;
  @media ${breakpointDesktopOnly} {
    font-size: 48px;
    margin-top: 42px;
    line-height: 60px;
    margin-bottom: 30px;
  }
`;

const HeroImageSection = styled.div`
  @media ${breakpointMobileExtended} {
    margin: 0 -32px;
    background: ${compassColors.offWhite};
  }
  @media ${breakpointDesktopOnly} {
    max-width: 40%;
  }
  @media ${breakpointNavBarFull} {
    max-width: calc(min(750px, 50%));
  }
`;

const HeroImageDesktop = styled.img`
  border-radius: 15px 0 0 100px;
  width: 100%;
  right: 0;
  @media ${breakpointMobileExtended} {
    display: none;
  }
`;

const HeroCTA = styled(Button)`
  margin-top: 10px;
  margin-bottom: 26px;
  @media ${breakpointDesktopOnly} {
    margin-bottom: 100px;
    max-width: 340px;
  }
`;

const BenefitEntry = styled.div`
  display: flex;
  flex-direction: row;
  @media ${breakpointDesktopOnly} {
    max-width: 340px;
  }
`;

const BenefitText = styled.p`
  ${fontP3Regular}
  margin: 0;
  margin-bottom: 10px;
  padding-left: 10px;
`;

const HeroImageMobile = styled.img`
  display: block;
  border-radius: 100px 0 0 0;
  width: 100%;
  height: 100%;
  @media ${breakpointDesktopOnly} {
    display: none;
  }
`;

export default function ClinicalDescription({
  gotoNextPage,
}: ClinicalIntroContentProps) {
  const translationKey = isMetaTraffic() ? "meta" : "general";
  function benefitEntry(text: ReactNode) {
    return (
      <BenefitEntry>
        <img
          src={GreenCheckmark}
          css={{ marginBottom: "28px" }}
          alt="Checkmark"
        />
        <BenefitText>{text}</BenefitText>
      </BenefitEntry>
    );
  }

  return (
    <Container>
      <IntroSection>
        <HeroImageSection>
          <HeroImageMobile
            src={ClinicalOralHeroImageMobile}
            alt={i18n.t("introClinical:description:imageAlt")}
          />
        </HeroImageSection>
        <Title>{i18n.t("introClinical:description:title")}</Title>
        <FsaHsaEligibility css={{ marginBottom: "16px" }} />
        <div>
          {benefitEntry(
            <Text
              i18nKeyOrText={`t:introClinical:description:benefits:${translationKey}:text1`}
              components={{ span: <span /> }}
            />
          )}
          {benefitEntry(
            <Text
              i18nKeyOrText="t:introClinical:description:benefits:general:text2"
              components={{ span: <span /> }}
            />
          )}
          {benefitEntry(
            <Text i18nKeyOrText="t:introClinical:description:benefits:general:text3" />
          )}
        </div>
        <HeroCTA
          borderRadius="32px"
          onClick={() => {
            trackBuyflowEvent("BuyflowButtonClicked", {
              buttonName: "seeIfEligible",
              pageId: "noomMedLP",
            });
            gotoNextPage();
          }}
        >
          {i18n.t(`introClinical:description:cta`)}
        </HeroCTA>
      </IntroSection>
      <HeroImageSection>
        <HeroImageDesktop
          src={ClinicalOralHeroImageDesktop}
          alt={i18n.t("introClinical:description:imageAlt")}
        />
      </HeroImageSection>
    </Container>
  );
}
